import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ethers } from 'ethers';
import lbDialog from '../UI/Modals/lbDialog';
import { useWriteContract } from 'wagmi';
import { useAppKitAccount } from '@reown/appkit/react';
import { Bot, CheckCircle2, Clock, Coins, Play, Power, RefreshCw, ShieldAlert, Wallet, Zap } from 'lucide-react';
import { nftNodes, nftNodesCFG, provider } from '../../.config';
import nodeTaskAbi from '../../abi/ILuckblocksNodeTask.json';
import nodesCfgAbi from '../../abi/luckBlocksNodesCFG.json';
import nodesAbi from '../../abi/luckBlocksNodes.json';
import { useT } from '../../i18n';
import { PLATFORMS } from './platforms';

const REFRESH_MS = 30000;
const GROUPS = ['all', 'lotteries', 'games', 'synths'];
const AUTOMATION_DOCS = 'https://docs.luckblocks.com/auto-spin-draw/how-to-configure-automation';

const settle = (promise, fallback = null) => Promise.resolve(promise).catch(() => fallback);

const duration = (seconds) => {
  const s = Math.max(0, Math.round(seconds));
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m`;
  return `${s}s`;
};

const isGolden = (id) => id < 11;

/** Reads every platform's node task, its queue, the wallet's nodes and their earnings */
const useNodeTasks = (address) => {
  const [tasks, setTasks] = useState([]);
  const [myNodes, setMyNodes] = useState([]);
  const [walletNodes, setWalletNodes] = useState(0);
  const [earnings, setEarnings] = useState(0);
  const [chainOffset, setChainOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const cfg = useMemo(() => new ethers.Contract(nftNodesCFG, nodesCfgAbi.abi, provider), []);
  const nft = useMemo(() => (nftNodes ? new ethers.Contract(nftNodes, nodesAbi.abi, provider) : null), []);

  const refresh = useCallback(async () => {
    try {
      const [block, staked, inWallet, rows] = await Promise.all([
        settle(provider.getBlock('latest')),
        address ? settle(cfg.getStakedNodesFromUser(address), []) : [],
        address && nft ? settle(nft.getTokensFromAddress(address), []) : [],
        Promise.all(
          PLATFORMS.map(async (task) => {
            const platform = new ethers.Contract(task.address, nodeTaskAbi, provider);
            const golden = await settle(platform.nodeTaskGolden());
            // older deployments do not implement the standard interface
            if (golden === null) return { ...task, supported: false };
            const [check, last, interval, active, waiting, turnWindow, mine] = await Promise.all([
              settle(platform.checkNodeTask(), [false, '0x']),
              settle(platform.autoSpinTimestamp(), ethers.constants.Zero),
              settle(platform.interval(), ethers.constants.Zero),
              settle(cfg.getActiveNodes(task.address), []),
              settle(cfg.getWaitingNodes(task.address), []),
              settle(cfg.turnWindow(task.address), ethers.constants.Zero),
              address ? settle(cfg.checkNodeTask(address, task.address)) : null,
            ]);
            return {
              ...task,
              supported: true,
              golden,
              due: check[0],
              dueAt: last.add(interval).toNumber(),
              active: active.map(Number),
              waiting: waiting.map(Number),
              turnWindow: turnWindow.toNumber() || 300,
              canRun: mine ? mine.canRun : false,
              needsActivation: mine ? mine.needsActivation : true,
              turnNode: mine ? Number(mine.turnNode) : 0,
              performData: mine ? mine.performData : check[1],
            };
          })
        ),
      ]);
      const ids = staked.map(Number);
      const profits = await Promise.all(ids.map((id) => settle(cfg.nodes(id))));
      setEarnings(profits.reduce((sum, n) => sum + (n ? Number(ethers.utils.formatUnits(n.totalProfit, 6)) : 0), 0));
      if (block) setChainOffset(Number(block.timestamp) * 1000 - Date.now());
      setMyNodes(ids);
      setWalletNodes(inWallet.length);
      setTasks(rows);
    } catch (err) {
      console.error('[NodeTasks] read failed', err);
    } finally {
      setLoading(false);
    }
  }, [cfg, nft, address]);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, REFRESH_MS);
    return () => clearInterval(id);
  }, [refresh]);

  return { tasks, myNodes, walletNodes, earnings, chainOffset, loading, refresh };
};

/** Seconds on the chain clock, ticking every second */
const useChainNow = (offset) => {
  const [now, setNow] = useState(() => Math.floor((Date.now() + offset) / 1000));
  useEffect(() => {
    const id = setInterval(() => setNow(Math.floor((Date.now() + offset) / 1000)), 1000);
    return () => clearInterval(id);
  }, [offset]);
  return now;
};

/**
 * Where the wallet stands on a task, in words: the contract's line (nodes of the task's type still to run this
 * cycle, lowest id first, then the next cycle) and the turn windows since the task became due
 */
const turnInfo = (task, eligible, now) => {
  const source = task.active.length ? task.active : task.waiting;
  const line = source.filter((id) => isGolden(id) === task.golden).sort((a, b) => a - b);
  const elapsed = now - task.dueAt - 1;
  const turn = task.due && elapsed >= 0 ? Math.floor(elapsed / task.turnWindow) : 0;
  const intoWindow = elapsed >= 0 ? elapsed % task.turnWindow : 0;
  const mine = line.findIndex((id) => eligible.includes(id));
  const turnNode = task.turnNode || line[turn] || null;
  const doneThisCycle = task.active.length > 0 && mine === -1 && task.waiting.some((id) => eligible.includes(id));
  let eta = null;
  if (task.due && mine > turn) eta = (mine - turn) * task.turnWindow - intoWindow;
  else if (task.due && mine >= 0 && mine < turn && turn < line.length) eta = (line.length - turn) * task.turnWindow - intoWindow;
  return { line, turnNode, eta, doneThisCycle, openToAll: task.due && turn >= line.length };
};

const TaskCard = ({ task, myNodes, connected, now, onPerform }) => {
  const t = useT();
  const eligible = myNodes.filter((id) => isGolden(id) === task.golden);
  const info = turnInfo(task, eligible, now);
  const ran = task.active.length ? task.waiting.filter((id) => isGolden(id) === task.golden) : [];

  let status;
  let tone = 'text-white/70';
  if (!task.due) {
    status = task.dueAt > now ? t('tasks.nextIn', { time: duration(task.dueAt - now) }) : t('tasks.waitingCondition');
  } else if (connected && eligible.length && task.needsActivation) {
    status = t('tasks.activateToJoin');
    tone = 'text-amber-200';
  } else if (task.canRun) {
    status = info.openToAll ? t('tasks.openToAll') : t('tasks.yourTurn');
    tone = 'text-emerald-300';
  } else if (connected && info.doneThisCycle) {
    status = t('tasks.doneThisCycle');
  } else if (connected && eligible.length && info.eta !== null) {
    status = t('tasks.turnOf', { node: info.turnNode, time: duration(info.eta) });
  } else {
    status = info.turnNode ? t('tasks.turnOfOther', { node: info.turnNode }) : t('tasks.dueNow');
    tone = 'text-sky-200';
  }

  return (
    <div className={`lb-tile flex flex-col gap-3 p-4 ${task.canRun ? 'ring-1 ring-emerald-400/50' : ''}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 items-center gap-3">
          {task.image ? (
            <img src={task.image} alt="" aria-hidden="true" className="h-8 w-8 shrink-0 rounded-full" />
          ) : task.Icon ? (
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-white/10 bg-white/5">
              <task.Icon className="h-4 w-4 text-white/70" aria-hidden="true" />
            </span>
          ) : null}
          <div className="min-w-0">
            <p className="font-bold text-white">{t(task.name)}</p>
            <p className="text-xs text-white/50">{t(task.reward)}</p>
          </div>
        </div>
        <span
          className={`shrink-0 rounded-full border px-2.5 py-1 text-[0.6rem] font-bold uppercase tracking-[0.16em] ${
            task.golden ? 'border-amber-400/40 bg-amber-400/10 text-amber-200' : 'border-sky-400/30 bg-sky-400/10 text-sky-200'
          }`}
        >
          {task.golden ? t('nodes.golden') : t('nodes.common')}
        </span>
      </div>

      <p className={`inline-flex items-center gap-1.5 text-sm font-semibold ${tone}`}>
        <Clock className="h-4 w-4 shrink-0" aria-hidden="true" /> {status}
      </p>

      <p className="notranslate text-xs text-white/45">
        {t('tasks.lineNow')}: {info.line.length ? info.line.map((n) => `#${n}`).join(' ') : '—'}
        {ran.length ? ` · ${t('tasks.ranThisCycle')}: ${ran.map((n) => `#${n}`).join(' ')}` : ''}
      </p>

      <div className="mt-auto flex flex-wrap items-center justify-end gap-2">
        {connected && !eligible.length ? (
          <span className="mr-auto text-xs text-white/40">{t(task.golden ? 'tasks.needGolden' : 'tasks.needCommon')}</span>
        ) : null}
        {eligible.length ? (
          task.needsActivation ? (
            <button className="lb-btn lb-btn-outline lb-btn-sm" onClick={() => onPerform(task, eligible)}>
              <Power className="mr-1 inline h-3.5 w-3.5" /> {t('tasks.activate')}
            </button>
          ) : (
            <button className="lb-btn lb-btn-primary lb-btn-sm" disabled={!task.canRun} onClick={() => onPerform(task, eligible)}>
              <Play className="mr-1 inline h-3.5 w-3.5" /> {t('tasks.run')}
            </button>
          )
        ) : null}
      </div>
    </div>
  );
};

const SummaryTile = ({ icon: Icon, label, value, children, tone = 'default' }) => (
  <div
    className={`flex flex-col gap-2 rounded-2xl border p-4 ${
      tone === 'go' ? 'border-emerald-400/40 bg-emerald-500/10' : tone === 'warn' ? 'border-amber-400/40 bg-amber-500/10' : 'border-white/10 bg-white/[0.03]'
    }`}
  >
    <p className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-white/50">
      <Icon className="h-4 w-4" aria-hidden="true" /> {label}
    </p>
    <p className="notranslate lb-num text-2xl font-bold text-white">{value}</p>
    {children}
  </div>
);

/**
 * NFT nodes control center: what the wallet can do now (run, activate, stake), its earnings, and every task its
 * nodes can run. Uses the standard node task interface, so a new platform only needs an entry in PLATFORMS.
 */
const NodeTasks = () => {
  const t = useT();
  const { address, isConnected } = useAppKitAccount();
  const { tasks, myNodes, walletNodes, earnings, chainOffset, loading, refresh } = useNodeTasks(address);
  const { writeContractAsync } = useWriteContract();
  const now = useChainNow(chainOffset);
  const [group, setGroup] = useState('all');
  const [scope, setScope] = useState('mine');
  const [batch, setBatch] = useState(null);

  const execute = async (task, nodes) => {
    const hash = await writeContractAsync({
      address: task.address,
      abi: nodeTaskAbi,
      functionName: 'performNodeTask',
      args: [nodes, task.performData || '0x'],
    });
    const receipt = await provider.waitForTransaction(hash);
    if (!receipt.status) throw new Error('Transaction failed on chain');
  };

  const perform = async (task, nodes) => {
    try {
      lbDialog.fire({
        title: task.needsActivation ? t('tasks.activating') : t('tasks.running'),
        text: t(task.name),
        allowOutsideClick: false,
        showConfirmButton: false,
        didOpen: () => lbDialog.showLoading(),
      });
      await execute(task, nodes);
      lbDialog.fire({ title: t('tx.success'), text: t(task.name), icon: 'success', timer: 2500, showConfirmButton: false });
    } catch (err) {
      console.error('[NodeTasks] perform failed', err);
      lbDialog.fire(t('tx.error'), err.shortMessage || err.message, 'error');
    } finally {
      refresh();
    }
  };

  /** One transaction per task, in order; stops at the first error */
  const performAll = async (list, kind) => {
    setBatch(kind);
    let done = 0;
    try {
      for (const task of list) {
        lbDialog.fire({
          title: t(kind === 'run' ? 'tasks.runningMany' : 'tasks.activatingMany', { n: done + 1, total: list.length }),
          text: t(task.name),
          allowOutsideClick: false,
          showConfirmButton: false,
          didOpen: () => lbDialog.showLoading(),
        });
        await execute(task, myNodes.filter((id) => isGolden(id) === task.golden));
        done += 1;
      }
      lbDialog.fire({ title: t('tx.success'), text: t('tasks.doneMany', { n: done }), icon: 'success', timer: 2500, showConfirmButton: false });
    } catch (err) {
      console.error('[NodeTasks] batch failed', err);
      lbDialog.fire(t('tx.error'), `${t('tasks.doneMany', { n: done })} · ${err.shortMessage || err.message}`, 'error');
    } finally {
      setBatch(null);
      refresh();
    }
  };

  const supported = tasks.filter((task) => task.supported);
  const hasNodes = isConnected && myNodes.length > 0;
  const eligibleFor = (task) => myNodes.some((id) => isGolden(id) === task.golden);
  const mineOnly = hasNodes && scope === 'mine';
  const shown = supported.filter((task) => (group === 'all' || task.group === group) && (!mineOnly || eligibleFor(task)));
  const ready = supported.filter((task) => eligibleFor(task) && !task.needsActivation && task.canRun);
  const toActivate = supported.filter((task) => eligibleFor(task) && task.needsActivation);
  const golden = myNodes.filter(isGolden);
  const common = myNodes.filter((id) => !isGolden(id));

  return (
    <section className="rounded-[32px] border border-white/10 bg-black/50 p-4 text-white shadow-2xl shadow-black/40 sm:p-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-amber-300">{t('tasks.eyebrow')}</p>
          <h2 className="mt-2 text-2xl font-display sm:text-3xl">{t('tasks.centerTitle')}</h2>
          <p className="mt-2 max-w-2xl text-sm text-white/60">{t('tasks.centerBlurb')}</p>
        </div>
        <button className="lb-btn lb-btn-ghost lb-btn-sm" onClick={refresh} aria-label={t('tasks.refresh')}>
          <RefreshCw className="h-4 w-4" />
        </button>
      </div>

      {/* ---------- what to do now ---------- */}
      {!isConnected ? (
        <div className="mt-5 flex flex-col items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.03] p-6 text-center">
          <Wallet className="h-8 w-8 text-amber-300" aria-hidden="true" />
          <p className="max-w-md text-sm text-white/65">{t('tasks.connectCenter')}</p>
          <appkit-button />
        </div>
      ) : (
        <div className="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <SummaryTile icon={Zap} label={t('tasks.readyNow')} value={ready.length} tone={ready.length ? 'go' : 'default'}>
            <button
              className="lb-btn lb-btn-primary lb-btn-sm"
              disabled={!ready.length || batch !== null}
              onClick={() => performAll(ready, 'run')}
            >
              <Play className="mr-1 inline h-3.5 w-3.5" /> {t('tasks.runAll', { n: ready.length })}
            </button>
          </SummaryTile>
          <SummaryTile icon={Power} label={t('tasks.toActivate')} value={toActivate.length} tone={toActivate.length ? 'warn' : 'default'}>
            {toActivate.length ? (
              <button className="lb-btn lb-btn-outline lb-btn-sm" disabled={batch !== null} onClick={() => performAll(toActivate, 'activate')}>
                {t('tasks.activateAll', { n: toActivate.length })}
              </button>
            ) : (
              <p className="flex items-center gap-1.5 text-xs text-emerald-300">
                <CheckCircle2 className="h-3.5 w-3.5" /> {hasNodes ? t('tasks.allActive') : t('tasks.noStakedNodes')}
              </p>
            )}
          </SummaryTile>
          <SummaryTile icon={Coins} label={t('tasks.earned')} value={`${earnings.toFixed(2)} USDT`}>
            <p className="notranslate text-xs text-white/50">
              {golden.length ? `${t('nodes.golden')}: ${golden.map((n) => `#${n}`).join(' ')}` : ''}
              {golden.length && common.length ? ' · ' : ''}
              {common.length ? `${t('nodes.common')}: ${common.map((n) => `#${n}`).join(' ')}` : ''}
              {!myNodes.length ? t('tasks.noStakedNodes') : ''}
            </p>
          </SummaryTile>
          <SummaryTile icon={Bot} label={t('tasks.automate')} value={t('tasks.automateValue')}>
            <a className="lb-link text-xs" href={AUTOMATION_DOCS} target="_blank" rel="noreferrer">
              {t('tasks.automateLink')}
            </a>
          </SummaryTile>
        </div>
      )}

      {isConnected && walletNodes > 0 ? (
        // a scroll, not a #link: the app routes on the URL hash
        <button
          type="button"
          onClick={() => document.getElementById('my-nodes')?.scrollIntoView({ behavior: 'smooth' })}
          className="mt-3 flex w-full items-center justify-between gap-3 rounded-2xl border border-amber-400/40 bg-amber-500/10 px-4 py-3 text-left text-sm text-amber-100"
        >
          {t('tasks.unstakedNodes', { n: walletNodes })}
          <span className="font-bold">{t('tasks.stakeNow')} →</span>
        </button>
      ) : null}

      {/* ---------- tasks ---------- */}
      <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
        <div className="lb-seg">
          {GROUPS.map((key) => (
            <button key={key} className="lb-seg-item" data-active={group === key} data-tone="gold" onClick={() => setGroup(key)}>
              {t(`tasks.group.${key}`)}
            </button>
          ))}
        </div>
        {hasNodes ? (
          <div className="lb-seg">
            {['mine', 'every'].map((key) => (
              <button key={key} className="lb-seg-item" data-active={scope === key} onClick={() => setScope(key)}>
                {t(key === 'mine' ? 'tasks.scopeMine' : 'tasks.scopeAll')}
              </button>
            ))}
          </div>
        ) : null}
      </div>

      {loading ? (
        <p className="mt-6 text-white/50">{t('common.loading')}</p>
      ) : shown.length ? (
        <div className="mt-5 grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {shown.map((task) => (
            <TaskCard key={task.key} task={task} myNodes={myNodes} connected={isConnected} now={now} onPerform={perform} />
          ))}
        </div>
      ) : (
        <p className="mt-6 flex items-center gap-2 text-sm text-white/55">
          <ShieldAlert className="h-4 w-4" /> {t('tasks.unsupported')}
        </p>
      )}

      {group === 'all' || group === 'synths' ? (
        <div className="mt-5 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-white/10 bg-white/[0.03] p-4">
          <div>
            <p className="font-bold">{t('tasks.liquidationsTitle')}</p>
            <p className="text-sm text-white/60">{t('tasks.liquidationsText')}</p>
          </div>
          <Link to="/token?section=cynths" className="lb-btn lb-btn-gold lb-btn-sm">
            {t('tasks.openLiquidations')}
          </Link>
        </div>
      ) : null}
    </section>
  );
};

export default NodeTasks;
